import { api, API } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";

/**
 * One attached file: name, download, and — only for the person who uploaded it — delete.
 *
 * Shared by all three portals so the rule reads the same wherever a file is listed. The server is
 * the actual enforcement (`DELETE /files/{fid}` in routers/shared.py); this only decides what to
 * offer, so admin never sees a bin next to the customer's evidence and vice versa.
 *
 * `locked` hides delete on a terminal case — a closed case is finished legal work and its
 * attachments stop changing with it (LIFECYCLE.md "Terminal cases are sealed").
 */
export default function FileRow({ file, locked, onDeleted, children, className = "" }) {
  const { user } = useAuth();
  const tok = localStorage.getItem("dt_token");
  const mine = !!user && file.uploaded_by === user.id;

  const del = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${file.name}"?\n\nThis removes it from the case for everyone.`)) return;
    try {
      await api.delete(`/files/${file.id}`);
      toast.success("File deleted");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete the file");
    }
  };

  return (
    <div className={`flex items-center justify-between gap-2 py-1.5 ${className}`} data-testid={`file-row-${file.id}`}>
      <a href={`${API}/files/${file.id}?_t=${tok}`} target="_blank" rel="noopener noreferrer"
         className="flex items-center gap-2 min-w-0 flex-1 hover:opacity-80">
        <span className="truncate">{file.name}</span>
        {children}
      </a>
      <div className="flex items-center gap-2 shrink-0">
        <a href={`${API}/files/${file.id}?_t=${tok}`} target="_blank" rel="noopener noreferrer"
           title="Download" aria-label="Download file" className="hover:opacity-80">
          <Download className="w-4 h-4"/>
        </a>
        {mine && !locked && (
          <button onClick={del} data-testid={`file-del-${file.id}`}
                  title="Delete this file" aria-label="Delete file"
                  className="text-[#6B7280] hover:text-[#F27373] transition-colors">
            <Trash2 className="w-4 h-4"/>
          </button>
        )}
      </div>
    </div>
  );
}
